interface IntegrationPartnerParams {
  userId: string | undefined
  clientId: string | undefined
}

interface IntegrationPartnerState {
  isConnecting: boolean
  isConnected: boolean
  error: string | null
  lastConnectedAt: string | null
  integrationPartnerName: string
}

export function useIntegrationPartner() {
  const state = reactive<IntegrationPartnerState>({
    isConnecting: false,
    isConnected: false,
    error: null,
    lastConnectedAt: null,
    integrationPartnerName: '',
  })

  const route = useRoute()
  const userStore = useUserStore()
  const { baseURL } = useRuntimeConfig().app

  const integrationParams = computed<IntegrationPartnerParams>(() => ({
    userId: route.query.userId as string | undefined,
    clientId: route.query.clientId as string | undefined,
  }))

  const isIntegrationFlow = computed(() =>
    !!integrationParams.value.userId && !!integrationParams.value.clientId,
  )

  const connectionStatus = computed(() => {
    if (state.isConnecting)
      return 'connecting'
    if (state.isConnected)
      return 'connected'
    return 'disconnected'
  })

  async function handleIntegration() {
    if (!isIntegrationFlow.value)
      return

    const { userId, clientId } = integrationParams.value
    if (!userId || !clientId)
      return

    try {
      state.isConnecting = true
      state.error = null

      await userStore.getProfileData()
      const handle = userStore.data?.handle

      if (!handle) {
        throw new Error('User not authenticated')
      }

      const response = await $fetch<{ integrationPartnerName: string }>(`/api/intern/customers/${handle}/link-to-integration-partner`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${unref(userStore.accessToken)}` },
        body: { userId, clientId },
        baseURL,
      })
      state.integrationPartnerName = response.integrationPartnerName || ''

      state.isConnected = true
      state.lastConnectedAt = new Date().toISOString()
    }
    catch (err) {
      state.error = 'Failed to link integration partner account'
      throw err
    }
    finally {
      state.isConnecting = false
    }
  }

  function reset() {
    state.isConnecting = false
    state.isConnected = false
    state.error = null
    state.lastConnectedAt = null
  }

  return {
    ...toRefs(state),
    connectionStatus,
    isIntegrationFlow,
    handleIntegration,
    reset,
  }
}
